import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthContext } from 'components/Context/AuthContext';
import DemoModal from 'components/DemoModal';
import LoginModal from 'components/LoginModal';
import '../../styles/TailRiskGlobal.css';


export default function TailRiskGlobal() {
  // Modal open/close
  const [demoOpen, setDemoOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  // Nav
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [tutTipVisible, setTutTipVisible] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Contact modal context (title + subject placeholder change per trigger)
  const [contactTitle, setContactTitle] = useState('Contact Us');
  const [contactSubjectPlaceholder, setContactSubjectPlaceholder] = useState('How can we help?');
  // FAQ accordion
  const [openFaq, setOpenFaq] = useState(null);
  // Contact form fields + status
  const [contactFirst, setContactFirst] = useState('');
  const [contactLast, setContactLast] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactError, setContactError] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // Scroll: nav tint + scroll spy
  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
      const sections = document.querySelectorAll('section[id], div[id]');
      const offset = 100;
      let current = '';
      sections.forEach(sec => {
        if (window.scrollY >= sec.offsetTop - offset) current = sec.id;
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Body overflow (modals)
  useEffect(() => {
    document.body.style.overflow = (demoOpen || loginOpen || contactOpen || trialOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [demoOpen, loginOpen, contactOpen, trialOpen]);

  // Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      setDemoOpen(false); setLoginOpen(false); setContactOpen(false); setTrialOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Tutorials tooltip click-outside
  useEffect(() => {
    if (!tutTipVisible) return;
    const handler = (e) => {
      if (!e.target.closest('[data-tut-btn]')) setTutTipVisible(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [tutTipVisible]);

  // Modal helpers
  const openDemo = (e) => { if (e) e.preventDefault(); setDemoOpen(true); };
  const closeDemo = () => setDemoOpen(false);
  const openLogin = (e) => { if (e) e.preventDefault(); setLoginOpen(true); };
  const handleDashboardClick = (e) => {
    if (e) e.preventDefault();
    if (isAuthenticated) { navigate('/summary'); } else { setLoginOpen(true); }
  };
  const closeLogin = () => setLoginOpen(false);
  const openTrial = (e) => { if (e) e.preventDefault(); setTrialOpen(true); };
  const openContact = (title, placeholder, e) => {
    if (e) e.preventDefault();
    setContactTitle(title);
    setContactSubjectPlaceholder(placeholder);
    setContactOpen(true);
  };
  const closeContact = () => {
    setContactOpen(false); setContactError(false); setContactSuccess(false);
    setContactFirst(''); setContactLast(''); setContactEmail('');
    setContactSubject(''); setContactMessage('');
  };

  // Contact submit
  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactFirst || !contactLast || !contactEmail || !contactMessage) {
      setContactError(true); setContactSuccess(false); return;
    }
    setContactError(false);
    const subj = contactSubject || 'Website Enquiry';
    const ms = encodeURIComponent(`${subj} - ${contactFirst} ${contactLast}`);
    const mb = encodeURIComponent(`From: ${contactFirst} ${contactLast}\nEmail: ${contactEmail}\n\n${contactMessage}`);
    window.location.href = `mailto:info@tailriskglobal.com?subject=${ms}&body=${mb}`;
    setContactSuccess(true);
    setContactFirst(''); setContactLast(''); setContactEmail('');
    setContactSubject(''); setContactMessage('');
  };

  // FAQ toggle
  const toggleFaq = (id) => setOpenFaq(openFaq === id ? null : id);

  // Nav link active style
  const navLinkActive = (section) => activeSection === section
    ? { color: 'var(--gold-text)', borderBottom: '1px solid rgba(201,168,76,0.4)', paddingBottom: '2px' }
    : {};

  return (
    <>
      <Helmet>
        <title>Tail Risk Global — Institutional-Grade Portfolio Risk Intelligence</title>
        <meta name="description" content="TRG delivers institutional-grade risk intelligence at 40–60% lower cost than traditional platforms." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Barlow:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Barlow+Condensed:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>


<div className="trg-page">

        {/* ═══════════ NAV ═══════════ */}
        <nav style={{ background: navScrolled ? 'rgba(12,30,48,0.98)' : 'rgba(13,21,32,0.92)' }}>
          <div className="nav-inner">
            <a href="#hero" className="logo" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <svg width="28" height="20" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
              </svg>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                <span style={{ fontFamily: 'var(--font-cond)', fontSize: '1rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>TAIL RISK <span style={{ color: '#C9A84C' }}>GLOBAL</span></span>
              </div>
            </a>
            <ul className="nav-links">
              <li><a href="#hero" style={navLinkActive('hero')}>Home</a></li>
              <li><a href="#platform" style={navLinkActive('platform')}>Platform</a></li>
              <li><a href="#capabilities" style={navLinkActive('capabilities')}>Capabilities</a></li>
              <li><a href="#why" style={{ whiteSpace: 'nowrap', ...navLinkActive('why') }}>Why TRG</a></li>
              <li><a href="#security" style={navLinkActive('security')}>Security</a></li>
              <li><a href="#about" style={navLinkActive('about')}>About</a></li>
              <li><a href="#pricing" style={navLinkActive('pricing')}>Pricing</a></li>
              <li><a href="#faq" style={navLinkActive('faq')}>FAQ</a></li>
              <li>
                <div className="nav-tooltip-wrap">
                  <a href="#" data-tut-btn onClick={(e) => { e.preventDefault(); setTutTipVisible(v => !v); }} style={{ color: 'var(--text-2)' }}>Tutorials</a>
                  <span className={`nav-tooltip${tutTipVisible ? ' visible' : ''}`}>Coming Soon</span>
                </div>
              </li>
              <li><a href="#cta" style={navLinkActive('cta')}>Contact</a></li>
              <li>
                <a
                  href="#"
                  onClick={handleDashboardClick}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.4)', padding: '0.3rem 0.85rem', letterSpacing: '0.1em', transition: 'background 0.2s,border-color 0.2s', fontFamily: 'var(--font-cond)', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}
                  onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.1)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.8)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)'; }}
                >
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ flexShrink: 0 }}><rect x="1" y="1" width="10" height="10" rx="1.5" /><path d="M4 6h4M6 4v4" /></svg>
                  Dashboard
                </a>
              </li>
            </ul>
            {/* Mobile hamburger */}
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileNavOpen(v => !v)}
              style={{ display: 'none', position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem', flexDirection: 'column', gap: '5px', alignItems: 'center', justifyContent: 'center' }}
            >
              <span style={{ display: 'block', width: '22px', height: '2px', background: 'var(--text)', borderRadius: '2px' }}></span>
              <span style={{ display: 'block', width: '22px', height: '2px', background: 'var(--text)', borderRadius: '2px' }}></span>
              <span style={{ display: 'block', width: '22px', height: '2px', background: 'var(--text)', borderRadius: '2px' }}></span>
            </button>
            {mobileNavOpen && (
              <div style={{ position: 'absolute', top: '60px', right: 0, width: '240px', background: '#0a1520', border: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem 0', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
                {[
                  ['#hero', 'Home'],
                  ['#platform', 'Platform'],
                  ['#capabilities', 'Capabilities'],
                  ['#why', 'Why TRG'],
                  ['#security', 'Security'],
                  ['#about', 'About'],
                  ['#pricing', 'Pricing'],
                  ['#faq', 'FAQ'],
                  ['#cta', 'Contact'],
                ].map(([href, label]) => (
                  <a
                    key={href}
                    href={href}
                    onClick={() => setMobileNavOpen(false)}
                    style={{ display: 'block', fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', padding: '0.75rem 1.2rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                  >{label}</a>
                ))}
                <a
                  href="#"
                  onClick={(e) => { setMobileNavOpen(false); handleDashboardClick(e); }}
                  style={{ display: 'block', fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.7)', padding: '0.75rem 1.2rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                >Dashboard</a>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); setMobileNavOpen(false); openDemo(e); }}
                  style={{ display: 'block', fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.7)', padding: '0.75rem 1.2rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                >Request Demo</a>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); setMobileNavOpen(false); }}
                  style={{ display: 'block', fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.7)', padding: '0.75rem 1.2rem' }}
                >Tutorials — Coming Soon</a>
              </div>
            )}
          </div>
        </nav>

        <div className="nav-sub-bar">
          <div className="nav-sub-inner" style={{ gap: '0.8rem' }}>
            <a href="#" onClick={openTrial} className="nav-cta" style={{ background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold-text)' }}>Start Free Trial</a>
            <a href="#" onClick={openDemo} className="nav-cta">Request Demo</a>
          </div>
        </div>

        {/* ═══════════ HERO ═══════════ */}
        <section id="hero">
          <div className="hero-bg"></div>
          <div className="hero-grid"></div>
          <div className="hero-content">
            <div>
              <div className="hero-eyebrow">Revolutionizing Portfolio Risk Management</div>
              {/* Full wide wordmark logo */}
              <div style={{ marginBottom: '1.8rem', marginTop: '-0.2rem' }}>
                <svg viewBox="0 0 704 119" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 'min(480px,100%)', height: 'auto' }}>
                  <path d="M103.49 19.94L131.19 47.64L141.15 37.67L103.49 0L79.0302 24.45L148.76 94.18L144.12 98.82L74.5202 29.22L29.9302 73.81L39.9002 83.78L74.5202 49.16L144.12 118.76L168.7 94.18L98.9702 24.45L103.49 19.94Z" fill="#C9A84C" />
                  <path d="M24.8126 78.9342L14.9414 88.8054L24.9116 98.7756L34.7828 88.9044L24.8126 78.9342Z" fill="#C9A84C" />
                  <path d="M9.86535 94.1051L-0.00585938 103.976L9.96434 113.947L19.8356 104.075L9.86535 94.1051Z" fill="#C9A84C" />
                  <path d="M220.93 38.3901H184.31V47.6101H196.99V80.5301H208.26V47.6101H220.93V38.3901Z" fill="#ffffff" />
                  <path d="M232.49 72.4601H246.38L248.81 80.5201H261.15L247.24 38.3901H231.6L217.69 80.5201H230.03L232.46 72.4601H232.49ZM239.27 49.9901H239.6L243.8 63.9001H235.07L239.27 49.9901Z" fill="#ffffff" />
                  <path d="M275.68 38.3901H264.24V80.5201H275.68V38.3901Z" fill="#ffffff" />
                  <path d="M310 71.3101H292.97V38.3901H281.53V80.5301H310V71.3101Z" fill="#ffffff" />
                  <path d="M336.64 66.6201H340.6L348 80.5301H360.43L351.87 64.8201C354.05 63.7701 355.77 62.2901 357.02 60.3501C358.36 58.2801 359.03 55.7301 359.03 52.7201C359.03 49.7101 358.38 47.1701 357.08 45.0301C355.78 42.8901 353.95 41.2501 351.61 40.1101C349.26 38.9701 346.53 38.4001 343.4 38.4001H325.21V80.5301H336.65V66.6201H336.64ZM336.64 47.5301H340.67C342.01 47.5301 343.16 47.7101 344.12 48.0801C345.07 48.4401 345.81 49.0101 346.32 49.7701C346.83 50.5301 347.09 51.5201 347.09 52.7201C347.09 53.9201 346.83 54.8801 346.32 55.6201C345.81 56.3601 345.07 56.9001 344.12 57.2301C343.17 57.5701 342.02 57.7301 340.67 57.7301H336.64V47.5301Z" fill="#ffffff" />
                  <path d="M375.26 38.3901H363.82V80.5201H375.26V38.3901Z" fill="#ffffff" />
                  <path d="M400.66 71.1901C399.84 71.4901 398.82 71.6401 397.59 71.6401C396.14 71.6401 394.88 71.4101 393.84 70.9601C392.79 70.5101 391.98 69.8401 391.4 68.9601C390.82 68.0801 390.5 67.0001 390.43 65.7101H379.57C379.58 69.2301 380.34 72.1301 381.84 74.3801C383.34 76.6401 385.45 78.3101 388.18 79.3901C390.9 80.4701 394.09 81.0201 397.76 81.0201C401.43 81.0201 404.38 80.5201 406.93 79.5201C409.47 78.5201 411.43 77.0601 412.8 75.1401C414.17 73.2201 414.86 70.8701 414.88 68.1001C414.87 66.5001 414.61 65.0101 414.11 63.6601C413.61 62.3001 412.83 61.0801 411.77 59.9901C410.71 58.9001 409.36 57.9501 407.7 57.1401C406.04 56.3301 404.04 55.6801 401.71 55.1901L398.25 54.4501C397.25 54.2401 396.38 54.0101 395.66 53.7501C394.93 53.4901 394.34 53.2001 393.87 52.8801C393.4 52.5601 393.06 52.1901 392.85 51.7901C392.64 51.3901 392.54 50.9301 392.57 50.4201C392.58 49.8001 392.77 49.2501 393.13 48.7701C393.49 48.2901 394.04 47.9101 394.79 47.6301C395.54 47.3501 396.5 47.2101 397.68 47.2101C399.53 47.2101 400.91 47.5901 401.83 48.3601C402.74 49.1301 403.25 50.2001 403.36 51.5701H414.3C414.29 48.8101 413.6 46.4001 412.25 44.3401C410.9 42.2801 408.98 40.6701 406.5 39.5401C404.02 38.4001 401.08 37.8301 397.67 37.8301C394.26 37.8301 391.38 38.3901 388.81 39.5201C386.24 40.6401 384.23 42.2101 382.78 44.2201C381.33 46.2301 380.62 48.5701 380.63 51.2501C380.62 54.5401 381.68 57.1401 383.83 59.0401C385.98 60.9401 388.91 62.2901 392.65 63.1001L396.85 64.0101C398.41 64.3501 399.65 64.7201 400.57 65.1101C401.49 65.5001 402.15 65.9401 402.56 66.4301C402.96 66.9201 403.17 67.4801 403.19 68.1301C403.18 68.8201 402.96 69.4301 402.53 69.9601C402.1 70.4901 401.48 70.9101 400.66 71.2101V71.1901Z" fill="#ffffff" />
                  <path d="M430.77 69.6701L434.56 64.7301L444.02 80.5301H457.68L443.12 57.1601L457.35 38.3901H444.02L431.35 55.4301H430.77V38.3901H419.34V80.5301H430.77V69.6701Z" fill="#ffffff" />
                  <path d="M493.51 65.06H501.16C501.13 66.24 500.85 67.26 500.3 68.15C499.72 69.08 498.86 69.8 497.7 70.31C496.54 70.82 495.09 71.07 493.35 71.07C491.38 71.07 489.72 70.6 488.39 69.67C487.06 68.74 486.06 67.39 485.39 65.64C484.72 63.88 484.38 61.77 484.38 59.3C484.38 56.83 484.73 54.77 485.44 53.05C486.15 51.32 487.17 50 488.53 49.08C489.88 48.16 491.52 47.7 493.44 47.7C494.33 47.7 495.15 47.8 495.9 48C496.65 48.2 497.31 48.49 497.87 48.88C498.44 49.27 498.92 49.74 499.3 50.3C499.68 50.86 499.97 51.5 500.14 52.22H511.7C511.48 50.11 510.86 48.17 509.85 46.4C508.83 44.63 507.5 43.1 505.85 41.8C504.2 40.5 502.3 39.5 500.15 38.8C498 38.09 495.68 37.74 493.19 37.74C490.35 37.74 487.69 38.22 485.21 39.17C482.73 40.12 480.54 41.53 478.65 43.39C476.76 45.25 475.28 47.52 474.22 50.21C473.16 52.9 472.63 55.98 472.63 59.47C472.63 63.91 473.5 67.75 475.23 70.97C476.97 74.19 479.38 76.68 482.48 78.42C485.58 80.16 489.18 81.03 493.28 81.03C496.96 81.03 500.22 80.31 503.06 78.86C505.91 77.41 508.14 75.33 509.77 72.61C511.4 69.89 512.21 66.61 512.21 62.77V56.93H493.53V65.08L493.51 65.06Z" fill="#C9A84C" />
                  <path d="M528.65 38.3101H517.21V80.4501H545.69V71.2301H528.65V38.3101Z" fill="#C9A84C" />
                  <path d="M578.49 40.22C575.39 38.57 571.93 37.74 568.12 37.74C564.31 37.74 560.81 38.57 557.72 40.22C554.63 41.87 552.17 44.31 550.35 47.52C548.53 50.74 547.62 54.69 547.62 59.38C547.62 64.07 548.53 67.98 550.35 71.2C552.17 74.42 554.62 76.86 557.72 78.52C560.81 80.19 564.28 81.02 568.12 81.02C571.96 81.02 575.39 80.19 578.49 78.54C581.59 76.89 584.05 74.45 585.88 71.24C587.7 68.02 588.62 64.07 588.62 59.38C588.62 54.69 587.71 50.74 585.88 47.52C584.06 44.3 581.59 41.87 578.49 40.22ZM575.85 65.77C575.18 67.51 574.2 68.82 572.91 69.72C571.61 70.62 570.02 71.07 568.13 71.07C566.24 71.07 564.64 70.62 563.35 69.72C562.05 68.82 561.07 67.51 560.41 65.77C559.74 64.04 559.41 61.91 559.41 59.38C559.41 56.85 559.74 54.73 560.41 52.99C561.08 51.26 562.06 49.94 563.35 49.04C564.65 48.14 566.24 47.69 568.13 47.69C570.02 47.69 571.62 48.14 572.91 49.04C574.21 49.94 575.19 51.26 575.85 52.99C576.51 54.73 576.85 56.85 576.85 59.38C576.85 61.91 576.52 64.03 575.85 65.77Z" fill="#C9A84C" />
                  <path d="M623.05 59.84C621.57 59 619.92 58.5401 618.11 58.4701V58.0601C619.74 57.7301 621.17 57.14 622.39 56.28C623.61 55.42 624.56 54.3601 625.24 53.0801C625.92 51.8001 626.26 50.39 626.26 48.84C626.26 46.7 625.7 44.84 624.58 43.26C623.46 41.68 621.81 40.46 619.62 39.6C617.43 38.74 614.73 38.3 611.52 38.3H593.25V80.43H613.08C616.14 80.43 618.77 79.92 620.98 78.89C623.19 77.86 624.89 76.4501 626.09 74.6501C627.29 72.8501 627.89 70.8 627.89 68.5C627.89 66.51 627.45 64.7801 626.57 63.3101C625.69 61.8401 624.51 60.6801 623.03 59.8301L623.05 59.84ZM604.7 47.28H609.55C610.98 47.28 612.16 47.63 613.09 48.32C614.02 49.01 614.49 50.0101 614.49 51.3101C614.49 52.1701 614.27 52.9 613.84 53.5C613.41 54.1 612.81 54.55 612.04 54.87C611.27 55.19 610.39 55.34 609.39 55.34H604.7V47.28ZM614.37 70.2301C613.41 70.9501 611.97 71.31 610.05 71.31H604.7V62.42H610.21C611.38 62.42 612.37 62.6001 613.2 62.9501C614.03 63.3101 614.67 63.8301 615.12 64.5101C615.57 65.2001 615.8 66.03 615.8 67.02C615.8 68.43 615.32 69.5001 614.36 70.2201L614.37 70.2301Z" fill="#C9A84C" />
                  <path d="M642.65 38.3101L628.74 80.4401H641.08L643.51 72.3801H657.4L659.83 80.4401H672.17L658.26 38.3101H642.62H642.65ZM646.1 63.8201L650.3 49.9101H650.63L654.83 63.8201H646.1Z" fill="#C9A84C" />
                  <path d="M686.71 38.3101H675.27V80.4501H703.75V71.2301H686.71V38.3101Z" fill="#C9A84C" />
                </svg>
              </div>
              <h1 className="hero-h1">
                Institutional-Grade<br />Risk Intelligence.<br />
                <em>Redefined.</em>
              </h1>
              <p className="hero-tagline">
                TRG delivers <strong>institutional-grade risk intelligence at 40–60% lower cost</strong>
                {' '}than traditional platforms—paired with seamless onboarding and interactive dashboards
                updated with <strong>end-of-day market data</strong> for daily portfolio analysis and on-the-fly rebalancing.
              </p>
              <div className="hero-actions">
                <a href="#platform" className="btn-primary">Explore the Platform
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 7h10M8 3l4 4-4 4" /></svg>
                </a>
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              {/* Large decorative mark behind stat cards */}
              <div style={{ position: 'absolute', top: '-20px', right: '-30px', width: '200px', opacity: 0.05, pointerEvents: 'none', zIndex: 0 }}>
                <svg viewBox="0 0 206 145" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M125.96 24.27L159.67 57.98L171.8 45.85L125.96 0L96.1902 29.76L181.06 114.63L175.41 120.28L90.7002 35.57L36.4302 89.84L48.5602 101.97L90.7002 59.84L175.41 144.54L205.33 114.63L120.46 29.76L125.96 24.27Z" fill="#C9A84C" />
                  <path d="M30.1968 96.0763L18.1831 108.09L30.3171 120.224L42.3308 108.21L30.1968 96.0763Z" fill="#C9A84C" />
                  <path d="M12.0123 114.542L-0.00146484 126.555L12.1325 138.689L24.1462 126.676L12.0123 114.542Z" fill="#C9A84C" />
                </svg>
              </div>
              <div className="hero-stats" style={{ position: 'relative', zIndex: 1 }}>
                <div className="stat-card">
                  <div className="stat-num">40–60%</div>
                  <div className="stat-label">Lower cost vs. traditional platforms</div>
                </div>
                <div className="stat-card">
                  <div className="stat-num">Daily</div>
                  <div className="stat-label">
                    End-of-day market data refresh<br />
                    <span style={{ fontSize: '0.7rem', color: 'rgba(201,168,76,0.7)', fontStyle: 'italic' }}>Real-time updates — coming soon</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-num">3</div>
                  <div className="stat-label">Analytical lenses on one unified platform</div>
                </div>
                <div className="stat-card">
                  <div className="stat-num">Multi&#8209;Asset</div>
                  <div className="stat-label">Equities, fixed income, derivatives &amp; alts</div>
                </div>
              </div>
            </div>
          </div>
          <div className="scroll-cue">
            <svg viewBox="0 0 24 24" strokeWidth="1.5"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            Scroll
          </div>
        </section>

        {/* ═══════════ TICKER ═══════════ */}
        <div className="ticker-strip">
          <div className="ticker-track">
            <div className="ticker-item"><span className="dot"></span>Daily End-of-Day Market Data Updates</div>
            <div className="ticker-item"><span className="dot"></span>VaR &amp; Expected Shortfall</div>
            <div className="ticker-item"><span className="dot"></span>Stress Testing &amp; Scenario Analysis</div>
            <div className="ticker-item"><span className="dot"></span>Multi-Custodian Consolidation</div>
            <div className="ticker-item"><span className="dot"></span>On-the-Fly Rebalancing</div>
            <div className="ticker-item"><span className="dot"></span>Forward-Looking Diagnostics</div>
            <div className="ticker-item"><span className="dot"></span>Board-Ready Outputs</div>
            <div className="ticker-item"><span className="dot"></span>Risk Limit Monitoring</div>
            <div className="ticker-item"><span className="dot"></span>Illiquid Asset Tail Risk — Proprietary Methodology</div>
            <div className="ticker-item"><span className="dot"></span>Real-Time Updates — Coming Soon</div>
            {/* duplicate for seamless loop */}
            <div className="ticker-item"><span className="dot"></span>Daily End-of-Day Market Data Updates</div>
            <div className="ticker-item"><span className="dot"></span>VaR &amp; Expected Shortfall</div>
            <div className="ticker-item"><span className="dot"></span>Stress Testing &amp; Scenario Analysis</div>
            <div className="ticker-item"><span className="dot"></span>Multi-Custodian Consolidation</div>
            <div className="ticker-item"><span className="dot"></span>On-the-Fly Rebalancing</div>
            <div className="ticker-item"><span className="dot"></span>Forward-Looking Diagnostics</div>
            <div className="ticker-item"><span className="dot"></span>Board-Ready Outputs</div>
            <div className="ticker-item"><span className="dot"></span>Risk Limit Monitoring</div>
            <div className="ticker-item"><span className="dot"></span>Illiquid Asset Tail Risk — Proprietary Methodology</div>
            <div className="ticker-item"><span className="dot"></span>Real-Time Updates — Coming Soon</div>
          </div>
        </div>

        {/* ═══════════ PLATFORM ═══════════ */}
        <section id="platform">
          <div className="container">
            <div className="tag">The Platform</div>
            <h2 className="section-title fade-up">One Platform.<br />Three Analytical Lenses.</h2>
            <p className="section-sub fade-up d1">
              Every TRG subscription shares a unified data infrastructure. Choose the analytical
              depth that fits your mandate—and scale as your needs evolve.
            </p>
            <div className="lenses-grid">
              <div className="lens-card fade-up d1">
                <div className="lens-accent"></div>
                <div className="lens-number">01</div>
                <div className="lens-badge">Core Platform</div>
                <div className="lens-name">Market Value Lens</div>
                <div className="lens-sub">Daily Market Value Metrics</div>
                <p className="lens-desc">Your foundation. Consolidate positions across custodians and entities, visualize allocations updated each end-of-day, and model rebalancing scenarios on the fly.</p>
                <ul className="lens-features">
                  <li>Daily end-of-day market value &amp; allocation analytics</li>
                  <li>Multi-custodian &amp; multi-portfolio consolidation</li>
                  <li>Security-level drilldown</li>
                  <li>Rebalancing impact modeling</li>
                  <li>Automated, presentation-ready outputs</li>
                  <li>Power BI / Tableau export</li>
                  <li>Export to CSV for seamless integration with any AI tool or workflow</li>
                </ul>
              </div>
              <div className="lens-card featured fade-up d2">
                <div className="lens-accent"></div>
                <div className="lens-number">02</div>
                <div className="lens-badge">Risk Reports</div>
                <div className="lens-name">Periodic Risk Lens</div>
                <div className="lens-sub">Monthly · Quarterly · Semiannual · Annual</div>
                <p className="lens-desc">Snapshot risk intelligence for board reporting and investment committees. Deep diagnostics delivered on your schedule.</p>
                <ul className="lens-features">
                  <li>Volatility, VaR &amp; Expected Shortfall</li>
                  <li>Risk concentration by sector, country, factor</li>
                  <li>Top risk drivers &amp; vulnerabilities</li>
                  <li>Historical risk trends</li>
                  <li>Forward-looking diagnostics</li>
                  <li>Curated stress tests &amp; scenario analysis</li>
                </ul>
              </div>
              <div className="lens-card fade-up d3">
                <div className="lens-accent"></div>
                <div className="lens-number">03</div>
                <div className="lens-badge">Continuous Monitoring</div>
                <div className="lens-name">Daily Risk Lens</div>
                <div className="lens-sub">Daily End-of-Day Risk Intelligence</div>
                <p className="lens-desc">Full daily risk coverage with complete simulation control, refreshed each end-of-day. Real-time intraday updates on the roadmap.</p>
                <ul className="lens-features">
                  <li>Daily EOD risk metrics &amp; limit monitoring</li>
                  <li>Full concentration coverage</li>
                  <li>Complete simulation control</li>
                  <li>Comprehensive stress testing</li>
                  <li>Full rebalancing risk impact</li>
                  <li>Automated daily reporting</li>
                  <li style={{ color: 'rgba(201,168,76,0.7)', fontStyle: 'italic' }}>Real-time intraday updates — coming soon</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ VALUE BAND ═══════════ */}
        <div id="value">
          <div className="container">
            <div className="value-grid">
              <div className="value-card fade-up">
                <div className="value-num">40–60%</div>
                <div className="value-label">Cost reduction vs. legacy platforms</div>
              </div>
              <div className="value-card fade-up d1">
                <div className="value-num">EOD</div>
                <div className="value-label">Daily end-of-day data refresh — real-time coming soon</div>
              </div>
              <div className="value-card fade-up d2">
                <div className="value-num">Days</div>
                <div className="value-label">Typical onboarding timeline—not months</div>
              </div>
              <div className="value-card fade-up d3">
                <div className="value-num">All</div>
                <div className="value-label">Asset classes: equities, FI, derivatives, alts</div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ ILLIQUID ASSETS FEATURE ═══════════ */}
        <section id="illiquid">
          <div className="container">
            <div className="illiquid-grid">
              <div className="illiquid-left fade-up">
                <div className="illiquid-prop-badge">
                  <svg viewBox="0 0 10 10" fill="currentColor"><polygon points="5,0 10,10 0,10" /></svg>
                  Proprietary Methodology
                </div>
                <h2 className="illiquid-title">
                  Illiquid Assets.<br />
                  <em>Finally Measured Right.</em>
                </h2>
                <div className="illiquid-divider"></div>
                <p className="illiquid-body">
                  Most risk platforms treat private assets as a black box—smoothed returns, lagged valuations, and oversimplified diversification assumptions that <strong>understate true tail risk</strong>.
                </p>
                <p className="illiquid-body">
                  TRG's proprietary methodology delivers <strong>reliable estimates of the contribution of illiquid asset classes to portfolio tail loss diversification</strong>—giving investment committees and risk officers a genuinely complete picture of downside exposure across the entire portfolio.
                </p>
                <div className="illiquid-assets">
                  <span className="asset-pill">Private Equity</span>
                  <span className="asset-pill">Real Estate</span>
                  <span className="asset-pill">Private Debt</span>
                  <span className="asset-pill">Infrastructure</span>
                </div>
              </div>
              <div className="illiquid-right fade-up d2">
                <div className="illiquid-stat">
                  <div className="illiquid-stat-num">True Tail Risk</div>
                  <div className="illiquid-stat-label">Proprietary methodology that accounts for <strong>return smoothing</strong> and <strong>illiquidity-adjusted volatility and correlation</strong>—reflecting true co-movement and capturing dynamics that intensify during market stress, not just calm-market conditions.</div>
                </div>
                <div className="illiquid-stat">
                  <div className="illiquid-stat-num">Diversification Reality</div>
                  <div className="illiquid-stat-label">Measure the <strong>actual tail loss diversification contribution</strong> of each illiquid allocation—not the misleading low-volatility figure implied by lagged valuations.</div>
                </div>
                <div className="illiquid-stat">
                  <div className="illiquid-stat-num">Portfolio-Level View</div>
                  <div className="illiquid-stat-label">Illiquid exposure consolidated alongside liquid assets for a <strong>single, coherent risk picture</strong>—enabling true whole-portfolio stress testing and scenario analysis.</div>
                </div>
                <div className="illiquid-stat">
                  <div className="illiquid-stat-num">Rebalancing Intelligence</div>
                  <div className="illiquid-stat-label">Model the <strong>risk impact of changes to illiquid allocations</strong> before committing capital—including shifts in tail contribution, volatility and diversification at the total portfolio level. Exposure is governed by a user-defined <strong>capital deployment factor</strong> reflecting actual drawdown status, with <strong>full commitment</strong> and <strong>average exposure</strong> available as conservative and baseline presets.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CAPABILITIES ═══════════ */}
        <section id="capabilities">
          <div className="container">
            <div className="tag">Capabilities</div>
            <h2 className="section-title fade-up">Full Capability Matrix</h2>
            <p className="section-sub fade-up d1">
              Every feature, every tier—side by side. See exactly what you get at each level.
            </p>

            {/* TABLE 1: Platform Capabilities */}
            <div className="cap-section" style={{ marginTop: '3rem' }}>
              <div className="cap-section-title">Platform Capabilities — Shared Foundation</div>
              <div className="cap-table-wrap">
                <table className="cap-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Capability</th>
                      <th>Core Platform</th>
                      <th>Risk Reports</th>
                      <th>Continuous Monitoring</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Flexible Data Visualization (TRG Live Dashboards or Power BI / Tableau)</td><td><span className="check">✔</span></td><td><span className="note-cell">✔ Snapshot views</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Multi-Custodian &amp; Multi-Portfolio Consolidation</td><td><span className="check">✔</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Entity-Level Hierarchies</td><td><span className="check">✔</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Multi-Asset Coverage (incl. Derivatives &amp; Alternatives)</td><td><span className="check">✔</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Security-Level Drilldown</td><td><span className="check">✔</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Benchmark Tracking</td><td><span className="check">✔</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>On-the-Fly Portfolio Rebalancing</td><td><span className="note-cell">✔ Allocation impact</span></td><td><span className="note-cell">✔ Risk impact (snapshot)</span></td><td><span className="note-cell">✔ Full risk impact</span></td></tr>
                    <tr><td>Presentation-Ready Outputs</td><td><span className="note-cell">✔ Automated</span></td><td><span className="note-cell">✔ Board-ready</span></td><td><span className="note-cell">✔ Automated</span></td></tr>
                    <tr>
                      <td>Data Update Frequency</td>
                      <td><span className="note-cell">✔ Daily EOD</span></td>
                      <td><span className="note-cell">Monthly / Quarterly / Semiannual / Annual</span></td>
                      <td>
                        <span className="note-cell">✔ Daily EOD</span><br />
                        <span style={{ fontSize: '0.72rem', color: 'rgba(201,168,76,0.65)', fontStyle: 'italic' }}>Real-time — coming soon</span>
                      </td>
                    </tr>
                    <tr><td>Custom Modeling</td><td><span className="note-cell">✔ Expected return &amp; frequency</span></td><td><span className="note-cell">✔ Horizon, confidence, simulation</span></td><td><span className="note-cell">✔ Full simulation control</span></td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* TABLE 2: Data Aggregation */}
            <div className="cap-section">
              <div className="cap-section-title">Data Aggregation &amp; Portfolio Construction</div>
              <div className="cap-table-wrap">
                <table className="cap-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Capability</th>
                      <th>Core Platform</th>
                      <th>Risk Reports</th>
                      <th>Continuous Monitoring</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Data Aggregation &amp; Normalization</td><td><span className="check">✔</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>
                      Automatic Data Reconciliation
                      <br /><span style={{ fontSize: '0.78rem', color: 'var(--slate)', fontStyle: 'italic', fontWeight: 400 }}>Positions, valuations &amp; transactions reconciled automatically — requires direct custodian / broker data feed</span>
                    </td><td><span className="note-cell">✔ With direct feed</span></td><td><span className="note-cell">✔ With direct feed</span></td><td><span className="note-cell">✔ With direct feed</span></td></tr>
                    <tr><td>Allocation Across All Dimensions (sector, country, asset class, FX, security)</td><td><span className="note-cell">✔ Market value ($ / %)</span></td><td><span className="note-cell">✔ Risk exposure</span></td><td><span className="note-cell">✔ Risk exposure</span></td></tr>
                    <tr><td>Performance &amp; Contribution Analysis</td><td><span className="note-cell">✔ Return-based</span></td><td><span className="note-cell">✔ Risk contribution</span></td><td><span className="note-cell">✔ Risk contribution</span></td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* TABLE 3: Advanced Risk */}
            <div className="cap-section">
              <div className="cap-section-title">Advanced Risk Intelligence — Differentiation Layer</div>
              <div className="cap-table-wrap">
                <table className="cap-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Capability</th>
                      <th>Core Platform</th>
                      <th>Risk Reports</th>
                      <th>Continuous Monitoring</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Volatility, Expected Shortfall &amp; VaR</td><td><span className="dash">—</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Risk Concentration (sector, country, asset class, risk factor, security)</td><td><span className="dash">—</span></td><td><span className="note-cell">✔ Targeted analysis</span></td><td><span className="note-cell">✔ Full coverage</span></td></tr>
                    <tr><td>Top Risk Drivers &amp; Vulnerabilities</td><td><span className="dash">—</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Historical Risk Trends</td><td><span className="dash">—</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Forward-Looking Diagnostics</td><td><span className="dash">—</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Risk Limit Monitoring</td><td><span className="dash">—</span></td><td><span className="check">✔</span></td><td><span className="check">✔</span></td></tr>
                    <tr><td>Stress Testing &amp; Scenario Analysis</td><td><span className="dash">—</span></td><td><span className="note-cell">✔ Curated scenarios</span></td><td><span className="note-cell">✔ Comprehensive</span></td></tr>
                    <tr style={{ background: 'rgba(201,168,76,0.05)' }}>
                      <td style={{ color: 'var(--white)', fontWeight: 500 }}>
                        Illiquid Asset Tail Risk Contribution
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontFamily: 'var(--font-cond)', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--navy)', background: 'var(--gold)', padding: '0.15rem 0.45rem', marginLeft: '0.5rem', verticalAlign: 'middle' }}>Proprietary</span><br />
                        <span style={{ fontSize: '0.78rem', color: 'var(--slate)', fontStyle: 'italic', fontWeight: 400 }}>Return smoothing correction · illiquidity-adjusted volatility &amp; correlation · private equity, real estate, private debt, infrastructure</span>
                      </td>
                      <td><span className="dash">—</span></td>
                      <td><span className="check">✔</span></td>
                      <td><span className="check">✔</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ WHY TRG ═══════════ */}
        <section id="why">
          <div className="container">
            <div className="tag">Why TRG</div>
            <h2 className="section-title fade-up">Built Different.<br />Priced Differently.</h2>
            <div className="why-grid">
              <div className="why-card fade-up">
                <span className="why-icon">💰</span>
                <div className="why-title">Cost Efficiency Without Compromise</div>
                <p className="why-body">Institutional-grade analytics at 40–60% below legacy platform pricing. No feature trade-offs—just better economics for your mandate.</p>
                <div className="why-card-line"></div>
              </div>
              <div className="why-card fade-up d1">
                <span className="why-icon">⚡</span>
                <div className="why-title">Live, Interactive Dashboards</div>
                <p className="why-body">Analyze portfolios in real time, model rebalancing scenarios on the fly, and share board-ready outputs instantly—without waiting for batch reports.</p>
                <div className="why-card-line"></div>
              </div>
              <div className="why-card fade-up d2">
                <span className="why-icon">🚀</span>
                <div className="why-title">Seamless Onboarding</div>
                <p className="why-body">Connect custodians, normalize data, and go live in days—not months. TRG's structured onboarding process removes the friction of traditional implementation.</p>
                <div className="why-card-line"></div>
              </div>
              <div className="why-card fade-up d3">
                <span className="why-icon">🔭</span>
                <div className="why-title">One Platform, Every Lens</div>
                <p className="why-body">Start with the Core Platform and add periodic Risk Reports or Continuous Monitoring as your needs and sophistication evolve—no migrations required.</p>
                <div className="why-card-line"></div>
              </div>
              <div className="why-card fade-up d4">
                <span className="why-icon">🌐</span>
                <div className="why-title">Multi-Asset, Multi-Entity Ready</div>
                <p className="why-body">Full coverage across equities, fixed income, derivatives, and alternatives—consolidated across every custodian and entity in your structure.</p>
                <div className="why-card-line"></div>
              </div>
              <div className="why-card fade-up d5" style={{ border: '1px solid rgba(201,168,76,0.25)', position: 'relative', overflow: 'visible', paddingTop: '2.8rem' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--gold)', borderRadius: 0 }}></div>
                <div style={{ position: 'absolute', top: '0.7rem', left: '1.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'var(--font-cond)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--navy)', background: 'var(--gold)', padding: '0.22rem 0.6rem' }}>
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="currentColor"><polygon points="5,0 10,10 0,10" /></svg>
                  Proprietary
                </div>
                <span className="why-icon">🔒</span>
                <div className="why-title">Illiquid Asset Tail Risk Intelligence</div>
                <p className="why-body">Our proprietary methodology delivers reliable estimates of how private equity, real estate, private debt and infrastructure contribute to <strong style={{ color: 'var(--white)' }}>portfolio tail loss diversification</strong>—correcting for return smoothing and applying illiquidity-adjusted volatility and correlation to capture true co-movement, including dynamics that intensify under stress.</p>
                <div className="why-card-line"></div>
              </div>
              <div className="why-card fade-up d1">
                <span className="why-icon">🔬</span>
                <div className="why-title">Forward-Looking by Design</div>
                <p className="why-body">Stress tests, scenario analysis, and forward-looking diagnostics are core features—not expensive bolt-ons charged separately on legacy platforms.</p>
                <div className="why-card-line"></div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ SECURITY ═══════════ */}
        <section id="security">
          <div className="container">
            <div className="security-header">
              <div className="tag" style={{ display: 'inline-block' }}>Security</div>
              <h2 className="section-title fade-up">Enterprise-Grade Security.<br /><span style={{ color: 'var(--gold)' }}>Powered by AWS.</span></h2>
              <p className="section-sub fade-up d1">
                TRG is built on AWS enterprise-grade cloud infrastructure with security controls operating continuously across every layer of the platform.
              </p>
            </div>
            <div className="security-grid">
              {/* Card 1 */}
              <div className="sec-card fade-up d1">
                <div className="sec-logo-row">
                  <div className="sec-aws-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF9900"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                    <span>AWS</span>
                  </div>
                </div>
                <span className="why-icon">🛡️</span>
                <div>
                  <div className="sec-service-sub">Threat Protection</div>
                  <div className="sec-service-name">Always-On Defense</div>
                </div>
                <p className="sec-desc">Continuous protection against network-level and application-layer threats, with automated detection and mitigation operating around the clock — no manual intervention required.</p>
              </div>
              {/* Card 2 */}
              <div className="sec-card fade-up d2">
                <div className="sec-logo-row">
                  <div className="sec-aws-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF9900"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                    <span>AWS</span>
                  </div>
                </div>
                <span className="why-icon">🔒</span>
                <div>
                  <div className="sec-service-sub">Data Protection</div>
                  <div className="sec-service-name">Encryption End to End</div>
                </div>
                <p className="sec-desc">All data is encrypted in transit and at rest. Access is governed by strict authentication controls, ensuring your portfolio data is never exposed to unauthorized parties.</p>
              </div>
              {/* Card 3 */}
              <div className="sec-card fade-up d3">
                <div className="sec-logo-row">
                  <div className="sec-aws-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF9900"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                    <span>AWS</span>
                  </div>
                </div>
                <span className="why-icon">📡</span>
                <div>
                  <div className="sec-service-sub">Monitoring &amp; Response</div>
                  <div className="sec-service-name">Continuous Observability</div>
                </div>
                <p className="sec-desc">Infrastructure, application health and security events are monitored in real time. Anomalies trigger automated incident response — maintaining platform integrity and client data security at all times.</p>
              </div>
            </div>
            {/* Compliance strip */}
            <div className="security-strip fade-up">
              <div className="strip-item">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M8 1l5 2v5c0 3-2.5 5.5-5 6.5C5.5 13.5 3 11 3 8V3z" /></svg>
                End-to-End Encryption
              </div>
              <div className="strip-divider"></div>
              <div className="strip-item">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="8" cy="8" r="6" /><path d="M8 5v3l2 2" /></svg>
                24 / 7 Threat Monitoring
              </div>
              <div className="strip-divider"></div>
              <div className="strip-item">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="6" width="12" height="8" rx="1.5" /><path d="M5 6V4a3 3 0 016 0v2" /></svg>
                Data Encrypted at Rest &amp; in Transit
              </div>
              <div className="strip-divider"></div>
              <div className="strip-item">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 8h12M8 2v12" /></svg>
                AWS Cloud Infrastructure
              </div>
              <div className="strip-divider"></div>
              <div className="strip-item">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 8l3 3 7-7" /></svg>
                Automated Incident Response
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ ABOUT ═══════════ */}
        <section id="about">
          <div className="container">
            <div className="tag">About TRG</div>
            <div className="about-grid">
              <div className="about-left fade-up">
                <h2 className="section-title" style={{ marginBottom: '1.6rem' }}>
                  Wall Street Expertise.<br />
                  <span style={{ color: 'var(--gold)' }}>Accessible to All.</span>
                </h2>
                <div className="about-pull-quote">
                  <p>"Bringing <em>cutting-edge risk modeling</em> techniques, similar to those used by the largest financial institutions, to the broader portfolio management community."</p>
                </div>
                <p className="about-body">
                  Tail Risk Global was founded by <strong>Wall Street seasoned professionals</strong> in quantitative risk modeling, with deep expertise developed at top-tier financial institutions across a wide range of asset classes.
                </p>
                <p className="about-body">
                  We built TRG because the most sophisticated risk analytics were locked inside the largest banks and asset managers—inaccessible to the broader investment community due to cost, complexity, and opacity. Our goal was to change that.
                </p>
                <p className="about-body">
                  The result is a <strong>fully transparent, intuitive, and conceptually sound</strong> platform that enables on-the-spot decision making—without requiring a team of quants to interpret the output.
                </p>
              </div>
              <div className="about-pillars fade-up d2">
                <div className="about-pillar">
                  <div className="pillar-num">01</div>
                  <div className="pillar-body">
                    <div className="pillar-title">Wall Street Pedigree</div>
                    <p className="pillar-text">Founded by <strong>quantitative risk professionals</strong> from top-tier financial institutions, with hands-on experience modeling risk across equities, fixed income, derivatives, and alternatives.</p>
                  </div>
                </div>
                <div className="about-pillar">
                  <div className="pillar-num">02</div>
                  <div className="pillar-body">
                    <div className="pillar-title">Democratizing Institutional Risk</div>
                    <p className="pillar-text">The same <strong>cutting-edge methodologies</strong> used by the world's largest financial institutions—now available to the broader portfolio management community at a fraction of the cost.</p>
                  </div>
                </div>
                <div className="about-pillar">
                  <div className="pillar-num">03</div>
                  <div className="pillar-body">
                    <div className="pillar-title">Transparency by Design</div>
                    <p className="pillar-text">Every model, every output is built to be <strong>fully transparent and conceptually sound</strong>—so portfolio managers can act with confidence, not just trust a black box.</p>
                  </div>
                </div>
                <div className="about-pillar">
                  <div className="pillar-num">04</div>
                  <div className="pillar-body">
                    <div className="pillar-title">Built for Decision Making</div>
                    <p className="pillar-text">Not a reporting tool—an <strong>on-the-spot decision engine</strong>. Intuitive enough for investment committees. Rigorous enough for quantitative risk teams.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ PRICING ═══════════ */}
        <section id="pricing">
          <div className="container">
            <div className="tag">Pricing</div>
            <h2 className="section-title fade-up">Transparent Pricing.<br />No AUM Surprises on the Core.</h2>
            <p className="section-sub fade-up d1">
              TRG's Core Platform fee is based on portfolio complexity—<em>not your AUM</em>.
              Risk module fees scale with AUM only when you add periodic or continuous risk intelligence.
            </p>
            {/* Model callout banners */}
            <div className="pricing-model-banner fade-up d2">
              <div className="pmb-card core-card">
                <div className="pmb-icon core-icon">📊</div>
                <div className="pmb-body">
                  <div className="pmb-label">Core Platform Fee</div>
                  <div className="pmb-title">Flat-Rate. Portfolio-Based. <br />Not AUM-Dependent.</div>
                  <div className="pmb-desc">Priced on <strong>number of portfolios, positions, and complexity</strong>. Your AUM has zero impact on the Core fee—giving smaller managers the same institutional analytics at a predictable cost.</div>
                </div>
              </div>
              <div className="pmb-card risk-card">
                <div className="pmb-icon risk-icon">⚖️</div>
                <div className="pmb-body">
                  <div className="pmb-label">Risk Module Fee</div>
                  <div className="pmb-title">AUM-Based. Tiered Rates.<br />Complexity-Adjusted.</div>
                  <div className="pmb-desc">Periodic and continuous risk intelligence fees scale with <strong>AUM × basis point rate</strong>, with rates stepping down at higher AUM. A complexity add-on applies for portfolios with derivatives &amp; alternatives exposure.</div>
                </div>
              </div>
            </div>
            {/* Scenario table */}
            <div className="pricing-table-wrap fade-up d3">
              <table className="pricing-main-table">
                <thead>
                  <tr className="col-header">
                    <th style={{ width: '30%' }}>
                      <div className="th-inner">
                        <span className="th-label">Client Profile</span>
                        <span className="th-sub">Illustrative examples</span>
                      </div>
                    </th>
                    <th style={{ width: '23.3%' }}>
                      <div className="th-inner">
                        <span className="th-label">Core Platform Only</span>
                        <span className="th-sub">Market value analytics</span>
                        <span className="th-badge badge-flat">Not AUM-based</span>
                      </div>
                    </th>
                    <th style={{ width: '23.3%' }}>
                      <div className="th-inner">
                        <span className="th-label">Core + Quarterly Risk</span>
                        <span className="th-sub">Periodic risk intelligence</span>
                        <span className="th-badge badge-aum">Risk fee: AUM-based</span>
                      </div>
                    </th>
                    <th style={{ width: '23.3%' }}>
                      <div className="th-inner">
                        <span className="th-label">Core + Continuous Monitoring</span>
                        <span className="th-sub">Always-on daily risk</span>
                        <span className="th-badge badge-aum">Risk fee: AUM-based</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* ROW A */}
                  <tr className="scenario-row">
                    <td className="profile-cell">
                      <div className="profile-letter">A</div>
                      <div className="profile-name">Small Manager,<br />Low Derivatives Usage</div>
                      <div className="profile-pills">
                        <span className="ppill">$250M AUM</span>
                        <span className="ppill">1,000 positions</span>
                        <span className="ppill">10 portfolios</span>
                        <span className="ppill">Low Derivatives Usage</span>
                      </div>
                    </td>
                    <td className="price-cell core-col">
                      <div className="price-total">$19,800</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $19,800</span>
                      </div>
                    </td>
                    <td className="price-cell risk-col">
                      <div className="price-total">$39,300</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $19,800</span><br />
                        <span className="risk">+ Risk: $19,500</span>
                      </div>
                    </td>
                    <td className="price-cell risk-col">
                      <div className="price-total gold">$49,800</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $19,800</span><br />
                        <span className="risk">+ Risk: $30,000</span>
                      </div>
                    </td>
                  </tr>
                  {/* ROW B */}
                  <tr className="scenario-row">
                    <td className="profile-cell">
                      <div className="profile-letter">B</div>
                      <div className="profile-name">Mid-Size Manager,<br />Medium Derivatives Usage</div>
                      <div className="profile-pills">
                        <span className="ppill">$750M AUM</span>
                        <span className="ppill">3,000 positions</span>
                        <span className="ppill">50 portfolios</span>
                        <span className="ppill complex">Medium Derivatives Usage</span>
                        <span className="ppill complex">Alternatives</span>
                      </div>
                    </td>
                    <td className="price-cell core-col">
                      <div className="price-total">$28,350</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $28,350</span>
                      </div>
                    </td>
                    <td className="price-cell risk-col">
                      <div className="price-total">$88,020</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $28,350</span><br />
                        <span className="risk">+ Risk: $59,670</span>
                      </div>
                    </td>
                    <td className="price-cell risk-col">
                      <div className="price-total gold">$120,150</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $28,350</span><br />
                        <span className="risk">+ Risk: $91,800</span>
                      </div>
                    </td>
                  </tr>
                  {/* ROW C */}
                  <tr className="scenario-row">
                    <td className="profile-cell">
                      <div className="profile-letter">C</div>
                      <div className="profile-name">Institutional Manager,<br />High Derivatives Usage</div>
                      <div className="profile-pills">
                        <span className="ppill">$5B AUM</span>
                        <span className="ppill">10,000 positions</span>
                        <span className="ppill">100 portfolios</span>
                        <span className="ppill complex">High Derivatives Usage</span>
                        <span className="ppill complex">Alternatives</span>
                      </div>
                    </td>
                    <td className="price-cell core-col">
                      <div className="price-total">$49,680</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $49,680</span>
                      </div>
                    </td>
                    <td className="price-cell risk-col">
                      <div className="price-total">$308,250</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $49,680</span><br />
                        <span className="risk">+ Risk: $258,570</span>
                      </div>
                    </td>
                    <td className="price-cell risk-col">
                      <div className="price-total gold">$447,480</div>
                      <div className="price-breakdown">
                        <span className="base">Core: $49,680</span><br />
                        <span className="risk">+ Risk: $397,800</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="pricing-footnote fade-up">
              All fees are annual. Onboarding is complimentary for mandates up to 10 portfolios, 1,000 positions and 5 custodians. A one-time onboarding fee of $5,000 applies above this threshold. All plans include up to <strong>7 users</strong>. Additional users: <strong>$1,500 per user / year</strong>. <strong>All plans include a 30-day free trial</strong> — no payment required until you are ready to subscribe. Trial access is subject to our <a href="/doc/TRG_Trial_Agreement.pdf" download style={{ color: 'var(--gold)', textDecoration: 'underline' }}>Trial Access Agreement</a>. <a href="#" onClick={(e) => openContact('Request a Custom Quote', 'Tell us about your mandate', e)} style={{ color: 'var(--gold)', fontWeight: 600, textDecoration: 'underline' }}>Contact us for a custom quote.</a>
            </div>
          </div>
        </section>

        {/* ═══════════ FAQ ═══════════ */}
        <section id="faq">
          <div className="container">
            <div className="tag">Investor FAQ</div>
            <h2 className="section-title fade-up">Questions We Hear.<br /><em style={{ fontStyle: 'normal', color: 'var(--gold-text)' }}>Answered Directly.</em></h2>
            <p className="section-sub fade-up d1">Common questions from investment committees, risk officers and prospective clients—answered without the sales gloss.</p>
            <div className="faq-grid">
              {/* Q1 */}
              <div className={`faq-item${openFaq === 'q1' ? ' open' : ''} full-width fade-up`}>
                <button className="faq-q" onClick={() => toggleFaq('q1')}>
                  <span className="faq-q-text">How can TRG be so much cheaper than established risk platforms?</span>
                  <span className="faq-q-icon">+</span>
                </button>
                <div className="faq-a">
                  <p>TRG was built cloud-native from day one. No data centers, no implementation armies, no feature bloat from capabilities built for $500B mandates you will never use. Our AUM-based pricing scales with your success rather than front-loading costs through large license and implementation fees. <strong>40–60% cheaper is not a discount—it is what institutional risk intelligence costs when you do not build it in 2001.</strong></p>
                </div>
              </div>
              {/* Q2 */}
              <div className={`faq-item${openFaq === 'q2' ? ' open' : ''} fade-up d1`}>
                <button className="faq-q" onClick={() => toggleFaq('q2')}>
                  <span className="faq-q-text">How quickly can we go live?</span>
                  <span className="faq-q-icon">+</span>
                </button>
                <div className="faq-a">
                  <p>Onboarding is measured in <strong>days, not months</strong>. Our data ingestion layer is purpose-built for multi-custodian consolidation across standard file formats, with no implementation project or professional services engagement required. Most clients are running live risk analytics within a single business week.</p>
                </div>
              </div>
              {/* Q3 */}
              <div className={`faq-item${openFaq === 'q3' ? ' open' : ''} fade-up d2`}>
                <button className="faq-q" onClick={() => toggleFaq('q3')}>
                  <span className="faq-q-text">Our board requires a recognized vendor name. How do we manage governance risk?</span>
                  <span className="faq-q-icon">+</span>
                </button>
                <div className="faq-a">
                  <p>Brand risk is real and we respect it. The most effective answer is a <strong>parallel run</strong>: operate TRG alongside your existing platform for one reporting cycle and compare outputs directly. Investment committees respond to evidence—demonstrating comparable or superior risk intelligence at materially lower cost is a stronger governance argument than vendor brand alone.</p>
                </div>
              </div>
              {/* Q4 */}
              <div className={`faq-item${openFaq === 'q4' ? ' open' : ''} full-width fade-up d1`}>
                <button className="faq-q" onClick={() => toggleFaq('q4')}>
                  <span className="faq-q-text">Does TRG account for co-movement across asset types?</span>
                  <span className="faq-q-icon">+</span>
                </button>
                <div className="faq-a">
                  <p>Yes. TRG simulates the co-movement across asset classes as implied by empirical time series, combined with historical and Monte Carlo simulation to drive shock severity. No factor model is imposed to drive correlations—the co-movement structure comes directly from the data. This hybrid approach deliberately addresses the two main weaknesses of a pure factor model implementation.</p>
                  <p><strong>Correlation instability.</strong> Pure factor models estimate covariances over recent history and apply them statically. In stress periods, correlations spike sharply and calm-period estimates systematically understate co-movement of losses. TRG's framework addresses this directly by giving users the ability to adjust correlations separately for normal and stressed regimes.</p>
                  <p><strong>Fat tails.</strong> Factor models that assume normally distributed returns understate tail severity. By combining historical simulation with Monte Carlo, TRG's shock severity estimates are not constrained by the normal distribution assumption.</p>
                </div>
              </div>
              {/* Q5 */}
              <div className={`faq-item${openFaq === 'q5' ? ' open' : ''} full-width fade-up d1`}>
                <button className="faq-q" onClick={() => toggleFaq('q5')}>
                  <span className="faq-q-text">How does TRG handle illiquid asset risk—private equity, real estate, private debt?</span>
                  <span className="faq-q-icon">+</span>
                </button>
                <div className="faq-a">
                  <p>Most platforms treat private assets as a black box. TRG's proprietary methodology <strong>corrects for return smoothing</strong> and applies <strong>illiquidity-adjusted volatility and correlation</strong>—so risk estimates reflect true co-movement including dynamics that intensify under stress.</p>
                  <p>Users control a <strong>capital deployment factor</strong> reflecting actual drawdown status, with full-commitment and average-exposure presets available. The full correlation matrix, including stress-regime adjustments, is user-modifiable.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CTA ═══════════ */}
        <section id="cta" style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Large watermark mark */}
          <div style={{ position: 'absolute', right: '-60px', top: '50%', transform: 'translateY(-50%)', width: '380px', opacity: 0.04, pointerEvents: 'none' }}>
            <svg viewBox="0 0 206 145" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M125.96 24.27L159.67 57.98L171.8 45.85L125.96 0L96.1902 29.76L181.06 114.63L175.41 120.28L90.7002 35.57L36.4302 89.84L48.5602 101.97L90.7002 59.84L175.41 144.54L205.33 114.63L120.46 29.76L125.96 24.27Z" fill="#C9A84C" />
              <path d="M30.1968 96.0763L18.1831 108.09L30.3171 120.224L42.3308 108.21L30.1968 96.0763Z" fill="#C9A84C" />
              <path d="M12.0123 114.542L-0.00146484 126.555L12.1325 138.689L24.1462 126.676L12.0123 114.542Z" fill="#C9A84C" />
            </svg>
          </div>
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="tag" style={{ display: 'inline-block' }}>Get Started</div>
            <h2 className="section-title fade-up">
              See TRG in Action.<br /><em style={{ fontStyle: 'normal', color: 'var(--gold)' }}>Schedule a Live Demo.</em>
            </h2>
            <p className="section-sub fade-up d1">
              Experience institutional-grade risk intelligence—live, interactive, and built for
              your portfolio. No commitments, no generic decks.
            </p>
            <div className="cta-actions fade-up d2">
              <a href="#" onClick={openDemo} className="btn-primary">
                Request a Demo
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 7h10M8 3l4 4-4 4" /></svg>
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════ FOOTER ═══════════ */}
        <footer>
          <div className="container">
            <div className="footer-inner">
              <div>
                <div className="logo" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '0.2rem' }}>
                  <svg width="48" height="34" viewBox="0 0 206 145" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <path d="M125.96 24.27L159.67 57.98L171.8 45.85L125.96 0L96.1902 29.76L181.06 114.63L175.41 120.28L90.7002 35.57L36.4302 89.84L48.5602 101.97L90.7002 59.84L175.41 144.54L205.33 114.63L120.46 29.76L125.96 24.27Z" fill="#C9A84C" />
                    <path d="M30.1968 96.0763L18.1831 108.09L30.3171 120.224L42.3308 108.21L30.1968 96.0763Z" fill="#C9A84C" />
                    <path d="M12.0123 114.542L-0.00146484 126.555L12.1325 138.689L24.1462 126.676L12.0123 114.542Z" fill="#C9A84C" />
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                    <span style={{ fontFamily: 'var(--font-cond)', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>TAIL RISK <span style={{ color: '#C9A84C' }}>GLOBAL</span></span>
                  </div>
                </div>
                <p className="footer-brand-desc">
                  Wall Street expertise. Institutional-grade risk intelligence. Built for the broader portfolio management community.
                </p>
              </div>
              <div>
                <div className="footer-col-title">Platform</div>
                <ul className="footer-links">
                  <li><a href="#platform">Core Platform</a></li>
                  <li><a href="#capabilities">Capabilities</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><a href="#illiquid">Illiquid Asset Methodology</a></li>
                </ul>
              </div>
              <div>
                <div className="footer-col-title">Company</div>
                <ul className="footer-links">
                  <li><a href="#about">About TRG</a></li>
                  <li><a href="#why">Why TRG</a></li>
                  <li><a href="#faq">FAQ</a></li>
                  <li><a href="#security">Security</a></li>
                  <li><a href="#" onClick={(e) => openContact('Contact Us', 'How can we help?', e)}>Contact Us</a></li>
                </ul>
              </div>
              <div>
                <div className="footer-col-title">Legal</div>
                <ul className="footer-links">
                  <li><a href="/doc/Disclaimer.pdf" target="_blank" rel="noopener noreferrer">Disclaimer</a></li>
                  <li><a href="/doc/Privacy_Policy_Terms_of_Use_1.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy &amp; Terms of Use</a></li>
                  <li><a href="/doc/TRG_Trial_Agreement.pdf" target="_blank" rel="noopener noreferrer">Trial Access Agreement</a></li>
                </ul>
              </div>
              <div>
                <div className="footer-col-title">Account</div>
                <ul className="footer-links">
                  <li><a href="#" onClick={handleDashboardClick} style={{ color: 'var(--gold)', fontWeight: 600 }}>Dashboard Login →</a></li>
                  <li><a href="#" onClick={openTrial}>Start Free Trial</a></li>
                  <li><a href="#" onClick={openDemo}>Request a Demo</a></li>
                  <li><a href="#" onClick={(e) => openContact('Support', 'Brief description of your issue', e)}>Support</a></li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <div style={{ width: '100%', paddingBottom: '1rem', borderBottom: '1px solid var(--border)', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-2)', opacity: 0.5, lineHeight: 1.65 }}>
                  The TRG platform and all outputs are provided for informational and risk management purposes only and do not constitute investment advice. Users are solely responsible for all investment decisions. <a href="/doc/Disclaimer.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold-text)', opacity: 0.7, textDecoration: 'underline', textUnderlineOffset: '2px' }}>See our full Disclaimer for details.</a>
                </p>
              </div>
              <div className="footer-copy">© 2025 Tail Risk Global. All rights reserved.</div>
              <div className="footer-copy">www.tailriskglobal.com</div>
            </div>
          </div>
        </footer>

        {/* ═══════════ TRIAL MODAL ═══════════ */}
        <div
          style={{ display: trialOpen ? 'flex' : 'none', position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(4,12,22,0.88)', backdropFilter: 'blur(6px)', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={(e) => { if (e.target === e.currentTarget) setTrialOpen(false); }}
        >
          <div style={{ background: '#0d1f36', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', padding: '2.5rem 2rem', maxWidth: '480px', width: '100%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => setTrialOpen(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold-text)', opacity: 0.8, marginBottom: '0.6rem' }}>30-Day Free Trial</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700, color: '#fff', marginBottom: '0.5rem', lineHeight: 1.3 }}>Start Your Free Trial</div>
            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '1.4rem' }}>To get started, please download, complete, and sign the Trial Access Agreement, then email the signed copy to <a href="mailto:info@tailriskglobal.com" style={{ color: 'var(--gold-text)' }}>info@tailriskglobal.com</a>. One of our representatives will reach out shortly to activate your access.</p>
            <a href="/doc/TRG_Trial_Agreement.pdf" download style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: 'var(--gold)', color: '#0a1118', fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.85rem 1.4rem', borderRadius: '4px', marginBottom: '1.2rem', textDecoration: 'none' }}>
              Download Trial Agreement
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 2v8M3 7l4 4 4-4" /><path d="M2 12h10" /></svg>
            </a>
            <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '4px', padding: '1rem 1.2rem', marginBottom: '1.2rem' }}>
              <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-text)', marginBottom: '0.5rem' }}>Next Steps</div>
              <ol style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.75, paddingLeft: '1.1rem' }}>
                <li>Download and review the Trial Access Agreement</li>
                <li>Complete and sign all required fields</li>
                <li>Email the signed agreement to <a href="mailto:info@tailriskglobal.com" style={{ color: 'var(--gold-text)' }}>info@tailriskglobal.com</a></li>
                <li>A TRG representative will contact you within one business day to activate your account</li>
              </ol>
            </div>
            <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)', lineHeight: 1.6, textAlign: 'center' }}>
              No payment required during the trial period.<br />
              Questions? <a href="mailto:info@tailriskglobal.com" style={{ color: 'rgba(201,168,76,0.6)' }}>info@tailriskglobal.com</a>
            </p>
          </div>
        </div>

        {/* ═══════════ DEMO MODAL ═══════════ */}
        <DemoModal open={demoOpen} onClose={closeDemo} onSignIn={openLogin} />

        {/* ═══════════ LOGIN MODAL ═══════════ */}
        <LoginModal open={loginOpen} onClose={closeLogin} onRequestDemo={openDemo} />

        {/* ═══════════ CONTACT MODAL ═══════════ */}
        <div className={`trg-overlay${contactOpen ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeContact(); }}>
          <div className="trg-modal" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <div className="modal-logo">
                <svg width="24" height="17" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" /></svg>
                <div className="modal-logo-text">
                  <div className="modal-logo-main">Tail Risk <span>Global</span></div>
                  <div className="modal-logo-sub">Get in Touch</div>
                </div>
              </div>
              <button className="modal-close" onClick={closeContact} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 2l14 14M16 2L2 16" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-title">{contactTitle}</div>
              <div className="modal-subtitle">We typically respond within one business day</div>
              {contactSuccess && <div className="modal-success" style={{ display: 'block' }}>&#10004;&nbsp;&nbsp;Message sent. We&#8217;ll be in touch shortly.</div>}
              {contactError && <div className="modal-error" style={{ display: 'block' }}>Please fill in your name, email and message.</div>}
              <form onSubmit={handleContactSubmit} noValidate>
                <div className="modal-row">
                  <div className="modal-field">
                    <label className="modal-label" htmlFor="contactFirst">First Name *</label>
                    <input className="modal-input" type="text" id="contactFirst" placeholder="Jane" required value={contactFirst} onChange={(e) => setContactFirst(e.target.value)} />
                  </div>
                  <div className="modal-field">
                    <label className="modal-label" htmlFor="contactLast">Last Name *</label>
                    <input className="modal-input" type="text" id="contactLast" placeholder="Smith" required value={contactLast} onChange={(e) => setContactLast(e.target.value)} />
                  </div>
                </div>
                <div className="modal-field">
                  <label className="modal-label" htmlFor="contactEmail">Email address *</label>
                  <input className="modal-input" type="email" id="contactEmail" placeholder="jane@yourfirm.com" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                </div>
                <div className="modal-field">
                  <label className="modal-label" htmlFor="contactSubject">Subject</label>
                  <input className="modal-input" type="text" id="contactSubject" placeholder={contactSubjectPlaceholder} value={contactSubject} onChange={(e) => setContactSubject(e.target.value)} />
                </div>
                <div className="modal-field">
                  <label className="modal-label" htmlFor="contactMessage">Message *</label>
                  <textarea className="modal-input modal-textarea" id="contactMessage" placeholder="Tell us what&#8217;s on your mind&#8230;" required value={contactMessage} onChange={(e) => setContactMessage(e.target.value)}></textarea>
                </div>
                <button type="submit" className="modal-submit">
                  Send Message
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 7h10M8 3l4 4-4 4" /></svg>
                </button>
              </form>
              <div className="modal-divider"></div>
              <div className="modal-footer-note">Or email us directly: <a href="mailto:info@tailriskglobal.com">info@tailriskglobal.com</a></div>
            </div>
          </div>
        </div>

        {/* ═══════════ TRIAL MODAL ═══════════ */}
        {trialOpen && (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setTrialOpen(false); }}
            style={{ display: 'flex', position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(4,12,22,0.88)', backdropFilter: 'blur(6px)', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          >
            <div style={{ background: '#0d1f36', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', padding: '2.5rem 2rem', maxWidth: '480px', width: '100%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
              <button onClick={() => setTrialOpen(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
              <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold-text)', opacity: 0.8, marginBottom: '0.6rem' }}>30-Day Free Trial</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700, color: '#fff', marginBottom: '0.5rem', lineHeight: 1.3 }}>Start Your Free Trial</div>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '1.4rem' }}>
                To get started, please download, complete, and sign the Trial Access Agreement, then email the signed copy to <a href="mailto:info@tailriskglobal.com" style={{ color: 'var(--gold-text)' }}>info@tailriskglobal.com</a>. One of our representatives will reach out shortly to activate your access.
              </p>
              <a href="/doc/TRG_Trial_Agreement.pdf" download style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: 'var(--gold)', color: '#0a1118', fontFamily: 'var(--font-cond)', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.85rem 1.4rem', borderRadius: '4px', marginBottom: '1.2rem', textDecoration: 'none' }}>
                Download Trial Agreement
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 2v8M3 7l4 4 4-4" /><path d="M2 12h10" /></svg>
              </a>
              <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '4px', padding: '1rem 1.2rem', marginBottom: '1.2rem' }}>
                <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-text)', marginBottom: '0.5rem' }}>Next Steps</div>
                <ol style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.75, paddingLeft: '1.1rem' }}>
                  <li>Download and review the Trial Access Agreement</li>
                  <li>Complete and sign all required fields</li>
                  <li>Email the signed agreement to <a href="mailto:info@tailriskglobal.com" style={{ color: 'var(--gold-text)' }}>info@tailriskglobal.com</a></li>
                  <li>A TRG representative will contact you within one business day to activate your account</li>
                </ol>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)', lineHeight: 1.6, textAlign: 'center' }}>
                No payment required during the trial period.<br />
                Questions? <a href="mailto:info@tailriskglobal.com" style={{ color: 'rgba(201,168,76,0.6)' }}>info@tailriskglobal.com</a>
              </p>
            </div>
          </div>
        )}

      </div>{/* /trg-page */}
    </>
  );
}
